import useWindowSize from '@hooks/use-window-size';
import { useMemo } from 'react';

type BreakpointValue = number | string;

export type BreakpointConfig<T> = {
  breakpoint: BreakpointValue;
  value: T;
};

export function parseBreakpoint(breakpoint: BreakpointValue): number {
  if (typeof breakpoint === 'number') {
    return breakpoint;
  }
  if (typeof breakpoint === 'string' && breakpoint.endsWith('px')) {
    return parseInt(breakpoint.slice(0, -2), 10);
  }
  throw new Error(`Invalid breakpoint value: ${breakpoint}`);
}

export function useBreakpointValue<T>(
  configs: BreakpointConfig<T>[],
  defaultValue: T
): T {
  const { width } = useWindowSize();

  return useMemo(() => {
    if (width === undefined) {
      return defaultValue;
    }

    const sortedConfigs = [...configs].sort(
      (a, b) => parseBreakpoint(b.breakpoint) - parseBreakpoint(a.breakpoint)
    );

    const matchedConfig = sortedConfigs.find(
      config => width >= parseBreakpoint(config.breakpoint)
    );

    return matchedConfig ? matchedConfig.value : defaultValue;
  }, [configs, defaultValue, width]);
}
